/**
 * RecordTrainingView - Full-page training record form
 * Follows the RecordDefectView pattern with SwipeableView and sectioned cards
 */

import React, { useState, useEffect } from 'react';
import { AlertCircle, Users, CheckSquare, Award, Shield, GraduationCap, Loader2, ChevronDown } from 'lucide-react';
import { useSwipeBack, SwipeIndicator } from '../../../../hooks/useSwipeBack';
// FT-357: shell-level back handler — bottom bar owns Back. Header BackButton removed.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';
import { TRAINING_CATEGORIES, COMPETENCY_LEVELS, STCW_CERTIFICATE_TYPES, calculateCertificateExpiry } from '../../../../config/training';

const RecordTrainingView = ({ onBack, onSave, crew = [], initialData = null, isSaving = false, editReason = '' }) => {
  // FT-357: register onBack with the shell-level MobileBottomBar.
  useSetBackHandler(onBack);

  const today = new Date().toISOString().split('T')[0];
  const currentTime = new Date().toTimeString().slice(0, 5);
  const { containerRef, swipeProgress } = useSwipeBack(onBack);

  // Multi-trainee state
  const [selectedTrainees, setSelectedTrainees] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [customTopic, setCustomTopic] = useState('');

  const [formData, setFormData] = useState({
    date: today,
    time: currentTime,
    duration: '60',
    trainer: '',
    competencyLevel: 'competent',
    description: '',
    materialsUsed: '',
    assessmentMethod: '',
    notes: ''
  });

  // Certificate state
  const [certificateIssued, setCertificateIssued] = useState(false);
  const [certificateType, setCertificateType] = useState('stcw'); // 'stcw' or 'custom'
  const [stcwCertType, setStcwCertType] = useState('');
  const [customCertName, setCustomCertName] = useState('');
  const [customCertExpiry, setCustomCertExpiry] = useState('');
  const [noExpiry, setNoExpiry] = useState(false);
  const [certificateNumber, setCertificateNumber] = useState('');

  // Validation errors
  const [errors, setErrors] = useState({});

  // Initialize form when editing
  useEffect(() => {
    if (initialData) {
      // Edit mode - handle both old and new format
      if (initialData.trainees) {
        setSelectedTrainees(initialData.trainees);
      } else if (initialData.trainee) {
        // Legacy format - find crew member by name
        const member = crew.find(c => (c.full_name || c.name) === initialData.trainee);
        if (member) {
          setSelectedTrainees([{
            crewId: member.id,
            name: member.full_name || member.name,
            position: member.position || ''
          }]);
        } else {
          setSelectedTrainees([{ name: initialData.trainee }]);
        }
      }

      setSelectedCategory(initialData.category || '');
      setSelectedTopic(initialData.topicId || '');
      setCustomTopic(initialData.category === 'custom' ? initialData.topic : '');
      setFormData({
        date: initialData.date || today,
        time: initialData.time || currentTime,
        duration: initialData.duration?.toString() || '60',
        trainer: initialData.trainer || '',
        competencyLevel: initialData.competencyLevel || 'competent',
        description: initialData.description || '',
        materialsUsed: initialData.materialsUsed || '',
        assessmentMethod: initialData.assessmentMethod || '',
        notes: initialData.notes || ''
      });

      // Certificate fields for edit mode
      if (initialData.certificateIssued && initialData.certificateDetails) {
        setCertificateIssued(true);
        setCertificateType(initialData.certificateDetails.type);
        if (initialData.certificateDetails.type === 'stcw') {
          setStcwCertType(initialData.certificateDetails.certId || '');
        } else {
          setCustomCertName(initialData.certificateDetails.name || '');
          if (initialData.certificateDetails.expiry) {
            setCustomCertExpiry(initialData.certificateDetails.expiry);
            setNoExpiry(false);
          } else {
            setNoExpiry(true);
          }
        }
        setCertificateNumber(initialData.certificateDetails.number || '');
      }
    }
  }, [initialData, crew, today, currentTime]);

  // Show all non-archived crew (includes both On Duty and Off Duty)
  const activeCrew = crew.filter(c => c.status !== 'archived');

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (selectedTrainees.length === 0) {
      newErrors.trainees = 'Please select at least one trainee';
    }
    if (!selectedCategory) {
      newErrors.category = 'Please select a training category';
    }
    if (selectedCategory && selectedCategory !== 'custom' && !selectedTopic) {
      newErrors.topic = 'Please select a training topic';
    }
    if (selectedCategory === 'custom' && !customTopic.trim()) {
      newErrors.customTopic = 'Please enter a custom topic';
    }
    if (!formData.trainer.trim()) {
      newErrors.trainer = 'Trainer/instructor is required';
    }
    if (!formData.date) {
      newErrors.date = 'Date is required';
    }

    // Certificate validation
    if (certificateIssued) {
      if (certificateType === 'stcw' && !stcwCertType) {
        newErrors.stcwCertType = 'Please select a certificate type';
      }
      if (certificateType === 'custom' && !customCertName.trim()) {
        newErrors.customCertName = 'Please enter a certificate name';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      const element = document.getElementById(firstErrorField);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
      return;
    }

    const topicData = selectedCategory === 'custom'
      ? { category: 'custom', topicId: null, topic: customTopic }
      : {
          category: selectedCategory,
          topicId: selectedTopic,
          topic: TRAINING_CATEGORIES[selectedCategory]?.topics.find(t => t.id === selectedTopic)?.name || ''
        };

    // Create training record with certificate info
    const trainingRecord = {
      ...formData,
      ...topicData,
      trainees: selectedTrainees,
      duration: parseInt(formData.duration) || 60,
      completedSuccessfully: true,
      certificateIssued,
      ...(certificateIssued && {
        certificateDetails: {
          type: certificateType,
          certId: certificateType === 'stcw' ? stcwCertType : null,
          name: certificateType === 'stcw'
            ? STCW_CERTIFICATE_TYPES.find(c => c.id === stcwCertType)?.name
            : customCertName,
          number: certificateNumber || null,
          expiry: certificateType === 'stcw'
            ? calculateCertificateExpiry(formData.date, STCW_CERTIFICATE_TYPES.find(c => c.id === stcwCertType)?.renewalYears || 5)
            : (noExpiry ? null : customCertExpiry || null)
        }
      })
    };

    // Note: DrillsModule.handleSaveTraining handles the actual save operation,
    // including edit detection, audit trail, and certificate creation.
    // We just pass the training record through onSave.
    onSave(trainingRecord);
  };

  return (
    <div className="min-h-screen bg-gray-50" ref={containerRef}>
      <SwipeIndicator progress={swipeProgress} />

      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-3">
            {/* FT-357: header BackButton removed — bottom bar owns Back. */}
            <div>
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-cyan-600" />
                {initialData ? 'Edit Training Record' : 'Record Onboard Training'}
              </h1>
              <p className="text-sm text-gray-500">
                Training Records
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      {/* FT-83: pb-24 md:pb-0 prevents Save/Cancel buttons from being hidden behind mobile bottom nav */}
      <form onSubmit={handleSubmit}>
        <div className="max-w-3xl mx-auto p-6 pb-24 md:pb-6 space-y-6">

          {/* Section 1: Trainees Selection */}
          <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            <div className="bg-slate-700 px-5 py-3">
              <h2 className="text-base font-semibold text-white flex items-center gap-2">
                <Users className="h-5 w-5 text-white" />
                Trainees <span className="text-red-300">*</span>
              </h2>
            </div>
            <div className="p-6">

            <div
              id="trainees"
              className={`border rounded-md p-3 max-h-48 overflow-y-auto space-y-2 ${
                errors.trainees ? 'border-red-300 ring-1 ring-red-300' : 'border-gray-300'
              }`}
            >
              {/* Select All */}
              <label className="flex items-center gap-2 pb-2 border-b border-gray-200">
                <input
                  type="checkbox"
                  checked={selectedTrainees.length === activeCrew.length && activeCrew.length > 0}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedTrainees(activeCrew.map(c => ({
                        crewId: c.id,
                        name: c.full_name || c.name,
                        position: c.position || ''
                      })));
                    } else {
                      setSelectedTrainees([]);
                    }
                    if (errors.trainees) {
                      setErrors(prev => ({ ...prev, trainees: '' }));
                    }
                  }}
                  className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                />
                <span className="text-sm font-medium text-gray-700">Select All</span>
              </label>

              {/* Individual crew */}
              {activeCrew.map(member => {
                const isSelected = selectedTrainees.some(t => t.crewId === member.id);
                return (
                  <label key={member.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedTrainees(prev => [...prev, {
                            crewId: member.id,
                            name: member.full_name || member.name,
                            position: member.position || ''
                          }]);
                        } else {
                          setSelectedTrainees(prev => prev.filter(t => t.crewId !== member.id));
                        }
                        if (errors.trainees) {
                          setErrors(prev => ({ ...prev, trainees: '' }));
                        }
                      }}
                      className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                    />
                    <span className="text-sm text-gray-900">{member.full_name || member.name}</span>
                    {member.position && (
                      <span className="text-xs text-gray-500">({member.position})</span>
                    )}
                  </label>
                );
              })}

              {activeCrew.length === 0 && (
                <p className="text-sm text-gray-500 py-2">No active crew members found</p>
              )}
            </div>

            {errors.trainees && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.trainees}
              </p>
            )}

            {selectedTrainees.length > 0 && (
              <p className="text-sm text-gray-500 mt-2 flex items-center gap-1">
                <CheckSquare className="h-3 w-3" />
                {selectedTrainees.length} crew member{selectedTrainees.length !== 1 ? 's' : ''} selected
              </p>
            )}
            </div>
          </section>

          {/* Section 2: Training Topic */}
          <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            <div className="bg-slate-700 px-5 py-3">
              <h2 className="text-base font-semibold text-white">Training Topic</h2>
            </div>
            <div className="p-6">

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Category */}
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    id="category"
                    value={selectedCategory}
                    onChange={(e) => {
                      setSelectedCategory(e.target.value);
                      setSelectedTopic('');
                      setCustomTopic('');
                      if (errors.category) {
                        setErrors(prev => ({ ...prev, category: '', topic: '', customTopic: '' }));
                      }
                    }}
                    className={`block w-full rounded-lg border py-2 px-3 pr-8 text-gray-900 bg-white ${
                      errors.category ? 'border-red-300 focus:ring-red-600 focus:border-red-600' : 'border-gray-300 focus:ring-cyan-600 focus:border-cyan-600'
                    } focus:ring-2 sm:text-sm appearance-none`}
                  >
                    <option value="">Select category</option>
                    {Object.values(TRAINING_CATEGORIES).map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
                {errors.category && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.category}
                  </p>
                )}
              </div>

              {/* Topic (conditional on category) */}
              {selectedCategory && selectedCategory !== 'custom' && (
                <div>
                  <label htmlFor="topic" className="block text-sm font-medium text-gray-700 mb-1">
                    Topic <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      id="topic"
                      value={selectedTopic}
                      onChange={(e) => {
                        setSelectedTopic(e.target.value);
                        if (errors.topic) {
                          setErrors(prev => ({ ...prev, topic: '' }));
                        }
                      }}
                      className={`block w-full rounded-lg border py-2 px-3 pr-8 text-gray-900 bg-white ${
                        errors.topic ? 'border-red-300 focus:ring-red-600 focus:border-red-600' : 'border-gray-300 focus:ring-cyan-600 focus:border-cyan-600'
                      } focus:ring-2 sm:text-sm appearance-none`}
                    >
                      <option value="">Select topic</option>
                      {TRAINING_CATEGORIES[selectedCategory]?.topics.map(topic => (
                        <option key={topic.id} value={topic.id}>{topic.name}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>
                  {errors.topic && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.topic}
                    </p>
                  )}
                </div>
              )}

              {/* Custom Topic (if category is custom) */}
              {selectedCategory === 'custom' && (
                <div>
                  <label htmlFor="customTopic" className="block text-sm font-medium text-gray-700 mb-1">
                    Custom Topic <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="customTopic"
                    type="text"
                    value={customTopic}
                    onChange={(e) => {
                      setCustomTopic(e.target.value);
                      if (errors.customTopic) {
                        setErrors(prev => ({ ...prev, customTopic: '' }));
                      }
                    }}
                    placeholder="Enter custom training topic"
                    className={`block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ${
                      errors.customTopic ? 'ring-red-300 focus:ring-red-600' : 'ring-gray-300 focus:ring-cyan-600'
                    } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm`}
                  />
                  {errors.customTopic && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.customTopic}
                    </p>
                  )}
                </div>
              )}

              {/* Date */}
              <div>
                <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                  max={today}
                  className={`block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ${
                    errors.date ? 'ring-red-300 focus:ring-red-600' : 'ring-gray-300 focus:ring-cyan-600'
                  } focus:ring-2 focus:ring-inset sm:text-sm`}
                />
                {errors.date && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.date}
                  </p>
                )}
              </div>

              {/* Time */}
              <div>
                <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-1">
                  Time
                </label>
                <input
                  id="time"
                  type="time"
                  value={formData.time}
                  onChange={(e) => handleInputChange('time', e.target.value)}
                  className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
                />
              </div>

              {/* Duration */}
              <div>
                <label htmlFor="duration" className="block text-sm font-medium text-gray-700 mb-1">
                  Duration (minutes)
                </label>
                <input
                  id="duration"
                  type="number"
                  value={formData.duration}
                  onChange={(e) => handleInputChange('duration', e.target.value)}
                  min="15"
                  max="480"
                  className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
                />
              </div>

              {/* Trainer */}
              <div>
                <label htmlFor="trainer" className="block text-sm font-medium text-gray-700 mb-1">
                  Trainer/Instructor <span className="text-red-500">*</span>
                </label>
                <input
                  id="trainer"
                  type="text"
                  value={formData.trainer}
                  onChange={(e) => handleInputChange('trainer', e.target.value)}
                  placeholder="Name of trainer or instructor"
                  className={`block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ${
                    errors.trainer ? 'ring-red-300 focus:ring-red-600' : 'ring-gray-300 focus:ring-cyan-600'
                  } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm`}
                />
                {errors.trainer && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.trainer}
                  </p>
                )}
              </div>

              {/* Competency Level */}
              <div className="sm:col-span-2">
                <label htmlFor="competencyLevel" className="block text-sm font-medium text-gray-700 mb-1">
                  Competency Level
                </label>
                <div className="relative">
                  <select
                    id="competencyLevel"
                    value={formData.competencyLevel}
                    onChange={(e) => handleInputChange('competencyLevel', e.target.value)}
                    className="block w-full rounded-lg border border-gray-300 py-2 px-3 pr-8 text-gray-900 bg-white focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600 sm:text-sm appearance-none"
                  >
                    {COMPETENCY_LEVELS.map(level => (
                      <option key={level.id} value={level.id}>
                        {level.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {COMPETENCY_LEVELS.find(l => l.id === formData.competencyLevel)?.description ||
                   'Assessment of trainee\'s competency after training'}
                </p>
              </div>
            </div>
            </div>
          </section>

          {/* Section 3: Training Details */}
          <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            <div className="bg-slate-700 px-5 py-3">
              <h2 className="text-base font-semibold text-white">Training Details</h2>
            </div>
            <div className="p-6">
            <div className="space-y-4">
              {/* Training Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Training Description
                </label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={3}
                  placeholder="Brief description of training content and objectives"
                  className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
                />
              </div>

              {/* Materials Used */}
              <div>
                <label htmlFor="materialsUsed" className="block text-sm font-medium text-gray-700 mb-1">
                  Materials Used
                </label>
                <textarea
                  id="materialsUsed"
                  value={formData.materialsUsed}
                  onChange={(e) => handleInputChange('materialsUsed', e.target.value)}
                  rows={2}
                  placeholder="e.g., Manuals, videos, equipment demonstrations"
                  className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
                />
              </div>

              {/* Assessment Method */}
              <div>
                <label htmlFor="assessmentMethod" className="block text-sm font-medium text-gray-700 mb-1">
                  Assessment Method
                </label>
                <textarea
                  id="assessmentMethod"
                  value={formData.assessmentMethod}
                  onChange={(e) => handleInputChange('assessmentMethod', e.target.value)}
                  rows={2}
                  placeholder="How was competency assessed? (e.g., practical demonstration, Q&A, written test)"
                  className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
                />
              </div>

              {/* Additional Notes */}
              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Notes
                </label>
                <textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  rows={3}
                  placeholder="Any additional observations or follow-up requirements"
                  className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
                />
              </div>
            </div>
            </div>
          </section>

          {/* Section 4: Certificate Issuance */}
          <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            <div className="bg-slate-700 px-5 py-3">
              <h2 className="text-base font-semibold text-white flex items-center gap-2">
                <Award className="h-5 w-5 text-white" />
                Certificate Issuance
              </h2>
            </div>
            <div className="p-6">
            {/* Certificate Issued Toggle */}
            <div className="flex items-center gap-6 mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="certIssued"
                  checked={!certificateIssued}
                  onChange={() => setCertificateIssued(false)}
                  className="text-cyan-600 focus:ring-cyan-500"
                />
                <span className="text-sm text-gray-700">No certificate</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="certIssued"
                  checked={certificateIssued}
                  onChange={() => setCertificateIssued(true)}
                  className="text-cyan-600 focus:ring-cyan-500"
                />
                <span className="text-sm text-gray-700">Yes, certificate issued</span>
              </label>
            </div>

            {/* Certificate Details (shown when issued) */}
            {certificateIssued && (
              <div className="pl-4 border-l-2 border-cyan-200 space-y-4">

                {/* Certificate Type */}
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="certType"
                      checked={certificateType === 'stcw'}
                      onChange={() => setCertificateType('stcw')}
                      className="text-cyan-600 focus:ring-cyan-500"
                    />
                    <span className="text-sm text-gray-700">STCW/WBC3</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="certType"
                      checked={certificateType === 'custom'}
                      onChange={() => setCertificateType('custom')}
                      className="text-cyan-600 focus:ring-cyan-500"
                    />
                    <span className="text-sm text-gray-700">Custom</span>
                  </label>
                </div>

                {/* STCW Certificate Dropdown */}
                {certificateType === 'stcw' && (
                  <div className="space-y-3">
                    <div>
                      <label htmlFor="stcwCertType" className="block text-sm font-medium text-gray-700 mb-1">
                        Certificate <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <select
                          id="stcwCertType"
                          value={stcwCertType}
                          onChange={(e) => {
                            setStcwCertType(e.target.value);
                            if (errors.stcwCertType) {
                              setErrors(prev => ({ ...prev, stcwCertType: '' }));
                            }
                          }}
                          className={`block w-full rounded-lg border py-2 px-3 pr-8 text-gray-900 bg-white ${
                            errors.stcwCertType ? 'border-red-300 focus:ring-red-600 focus:border-red-600' : 'border-gray-300 focus:ring-cyan-600 focus:border-cyan-600'
                          } focus:ring-2 sm:text-sm appearance-none`}
                        >
                          <option value="">Select certificate...</option>
                          {STCW_CERTIFICATE_TYPES.map(cert => (
                            <option key={cert.id} value={cert.id}>{cert.name}</option>
                          ))}
                        </select>
                        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                      </div>
                      {errors.stcwCertType && (
                        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {errors.stcwCertType}
                        </p>
                      )}
                    </div>
                    {stcwCertType && (
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <Shield className="h-3 w-3" />
                        Expiry: {calculateCertificateExpiry(formData.date, STCW_CERTIFICATE_TYPES.find(c => c.id === stcwCertType)?.renewalYears || 5) || 'No expiry'}
                        {STCW_CERTIFICATE_TYPES.find(c => c.id === stcwCertType)?.renewalYears !== -1 && ' (auto-calculated)'}
                      </p>
                    )}
                  </div>
                )}

                {/* Custom Certificate */}
                {certificateType === 'custom' && (
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="customCertName" className="block text-sm font-medium text-gray-700 mb-1">
                        Certificate Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="customCertName"
                        type="text"
                        value={customCertName}
                        onChange={(e) => {
                          setCustomCertName(e.target.value);
                          if (errors.customCertName) {
                            setErrors(prev => ({ ...prev, customCertName: '' }));
                          }
                        }}
                        placeholder="e.g., Fire Warden Level 2"
                        className={`block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ${
                          errors.customCertName ? 'ring-red-300 focus:ring-red-600' : 'ring-gray-300 focus:ring-cyan-600'
                        } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm`}
                      />
                      {errors.customCertName && (
                        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {errors.customCertName}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="customCertExpiry" className="block text-sm font-medium text-gray-700 mb-1">
                        Expiry Date
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          id="customCertExpiry"
                          type="date"
                          value={customCertExpiry}
                          onChange={(e) => setCustomCertExpiry(e.target.value)}
                          disabled={noExpiry}
                          className={`block flex-1 rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm ${
                            noExpiry ? 'bg-gray-100 text-gray-500' : ''
                          }`}
                        />
                        <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={noExpiry}
                            onChange={(e) => {
                              setNoExpiry(e.target.checked);
                              if (e.target.checked) setCustomCertExpiry('');
                            }}
                            className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                          />
                          <span className="text-sm text-gray-600">No expiry</span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* Certificate Number (optional, both types) */}
                <div>
                  <label htmlFor="certificateNumber" className="block text-sm font-medium text-gray-700 mb-1">
                    Certificate Number <span className="text-gray-400">(optional)</span>
                  </label>
                  <input
                    id="certificateNumber"
                    type="text"
                    value={certificateNumber}
                    onChange={(e) => setCertificateNumber(e.target.value)}
                    placeholder="e.g., STCW-2025-001"
                    className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
                  />
                </div>

                {/* Info text for multiple trainees */}
                {selectedTrainees.length > 1 && (
                  <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                    <CheckSquare className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-amber-700">
                      Certificate will be issued to all {selectedTrainees.length} selected trainees
                    </p>
                  </div>
                )}
              </div>
            )}
            </div>
          </section>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 sticky bottom-0 bg-gray-50 py-4">
            <button
              type="button"
              onClick={onBack}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 border border-transparent rounded-md hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
              {isSaving ? 'Saving...' : (initialData ? 'Update Training' : 'Save Training')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default RecordTrainingView;
